import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { parseQrFromLocation } from "@/lib/qrParse";
import { callTriggerSubmit } from "@/lib/functions";
import { useSession } from "@/hooks/useSession";
import { useSharedCart } from "@/hooks/useSharedCart";
import { useParticipant } from "@/hooks/useParticipant";
import { useMenu } from "@/hooks/useMenu";
import { useOrderHistory } from "@/hooks/useOrderHistory";
import { useStuckSessionRecovery } from "@/hooks/useStuckSessionRecovery";
import { useQuantityHistory } from "@/hooks/useQuantityHistory";
import { useConnectivity } from "@/hooks/useConnectivity";
import { useRestaurant } from "@/hooks/useRestaurant";
import { useActiveMenuSettings } from "@/hooks/useActiveMenuSettings";
import { useSubmitGate } from "@/hooks/useSubmitGate";
import { countBuffetTours } from "@/lib/tourCount";
import { useRemovalNotifier } from "@/hooks/useRemovalNotifier";
import { useParticipants } from "@/hooks/useParticipants";
import { hasRequiredChoice, resolveProductRubrics, useRubricCatalog } from "@/hooks/useRubricCatalog";
import { hasComposition } from "@/lib/composition";
import { flyImageToCart } from "@/lib/flyToCart";
import {
  getCategoryLayout,
  getRestaurantName,
  isALaCarte,
  isCartShown,
  isWaiterConfirmRequired,
} from "@/lib/restaurantSettings";

import { InAppBrowserBanner } from "@/components/InAppBrowserBanner";
import { CountdownOverlay } from "@/components/CountdownOverlay";
import { ConnectivityGuard } from "@/components/ConnectivityGuard";
import { ErrorToast } from "@/components/ErrorToast";
import { MenuList } from "@/components/MenuList";
import { SharedCartView } from "@/components/SharedCartView";
import { OrderHistoryDrawer } from "@/components/OrderHistoryDrawer";
import { ProductDetailModal } from "@/components/ProductDetailModal";
import { CartTriggerButton } from "@/components/CartTriggerButton";
import { ConfirmSubmitDialog } from "@/components/ConfirmSubmitDialog";
import { CategoryPicker } from "@/components/CategoryPicker";
import { SplashScreen } from "@/components/SplashScreen";
import { MainMenuDrawer } from "@/components/MainMenuDrawer";
import { LanguagePicker } from "@/components/LanguagePicker";
import { AllergenModal } from "@/components/AllergenModal";
import { ServiceModal } from "@/components/ServiceModal";
import { NoticeToast } from "@/components/NoticeToast";
import { OrderSentToast } from "@/components/OrderSentToast";
import { NicknameModal } from "@/components/NicknameModal";
import { AlertDialog } from "@/components/AlertDialog";
import { InvalidQrPage } from "./InvalidQrPage";
import { withImageVersion } from "@/lib/imageUrl";
import type { DraftCartLine, Product, SelectedRubric } from "@/lib/types";

const LANG_DISPLAY: Record<string, { flag: string; label: string }> = {
  fr: { flag: "🇫🇷", label: "Français" },
  en: { flag: "🇬🇧", label: "English" },
  "en-US": { flag: "🇬🇧", label: "English" },
  cn: { flag: "🇨🇳", label: "中文" },
  it: { flag: "🇮🇹", label: "Italiano" },
  es: { flag: "🇪🇸", label: "Español" },
  de: { flag: "🇩🇪", label: "Deutsch" },
  pt: { flag: "🇵🇹", label: "Português" },
  ja: { flag: "🇯🇵", label: "日本語" },
  ko: { flag: "🇰🇷", label: "한국어" },
};

const computeLineSubtotal = (line: { unitPrice: number; qty: number; options?: SelectedRubric[] }): number => {
  const extras = (line.options ?? []).reduce(
    (s, r) => s + r.data.reduce((ss, it) => ss + (it.unitPrice || 0) * (it.quantity || 1), 0),
    0
  );
  return (line.unitPrice + extras) * line.qty;
};

export const TablePage = (): JSX.Element => {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const parsed = useMemo(
    () => parseQrFromLocation(location.pathname, location.search),
    [location.pathname, location.search]
  );

  const online = useConnectivity();
  const session = useSession(parsed, online);
  const participant = useParticipant(parsed);
  const participantId = participant.id;
  const participants = useParticipants(parsed);
  const isFrozen = session.phase === "submitting";
  const cart = useSharedCart(parsed, participantId, isFrozen);
  const resto = useRestaurant(parsed);
  const activeMenuSettings = useActiveMenuSettings(session.qrCode, resto);
  const takeawayMenu = !!session.qrCode?.takeawayMenu;
  const menu = useMenu(
    parsed,
    takeawayMenu,
    resto?.settings?.availabilityHours,
    activeMenuSettings.allowedCategories
  );
  const rubricCatalog = useRubricCatalog(parsed, takeawayMenu);
  // Réglages lus au même chemin que la tablette (settings.* / informations.*).
  const aLaCarte = isALaCarte(resto);
  const restaurantName = getRestaurantName(resto);
  // Logo du header ; une URL en erreur de chargement retombe sur le nom.
  const [brokenLogo, setBrokenLogo] = useState<string | null>(null);
  const headerLogo = resto?.logo && resto.logo !== brokenLogo ? resto.logo : undefined;
  // Mode consultation (showSitInCart / showTakeAwayCart faux) : la carte se
  // consulte, mais ni panier, ni historique, ni compteurs, ni ajout.
  const cartShown = isCartShown(resto, takeawayMenu);
  const waiterConfirm = isWaiterConfirmRequired(resto);
  // Page unique ou catégories à gauche (admin), suivi en temps réel.
  const categoryLayout = getCategoryLayout(resto);
  const orders = useOrderHistory(parsed);
  const history = useQuantityHistory(parsed);

  useStuckSessionRecovery(parsed?.sessionId ?? null, session.pendingSubmit);

  // True while pendingSubmit is in RTDB, and for ~2 s after it clears, so the
  // subsequent draftCart wipe coming from executeSubmit doesn't trigger a
  // "removed by another participant" notice. The 2 s window covers RTDB
  // event-ordering races (pendingSubmit + draftCart removes are in a single
  // Promise.all server-side, but the client receives them as independent
  // events).
  const [submitInFlight, setSubmitInFlight] = useState(false);
  useEffect(() => {
    if (session.pendingSubmit) {
      setSubmitInFlight(true);
      return;
    }
    if (!submitInFlight) return;
    const id = window.setTimeout(() => setSubmitInFlight(false), 2_000);
    return () => clearTimeout(id);
  }, [session.pendingSubmit, submitInFlight]);

  const removal = useRemovalNotifier(
    cart.lines,
    participantId,
    menu.productsById,
    submitInFlight
  );

  const [submitting, setSubmitting] = useState(false);
  const [detailProduct, setDetailProduct] = useState<Product | null>(null);
  const detailSourceRef = useRef<Element | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [mainMenuOpen, setMainMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [allergenOpen, setAllergenOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [limitAlert, setLimitAlert] = useState<string | null>(null);
  const [orderSentOpen, setOrderSentOpen] = useState(false);
  const closeOrderSent = useCallback(() => setOrderSentOpen(false), []);
  const lastSentAtRef = useRef<number | null>(null);

  // Measure the actual rendered top header height so the sticky category bars
  // sit flush against it (instead of using a hardcoded value that breaks under
  // safe-area-inset, long restaurant names, etc.).
  const headerRef = useRef<HTMLElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState<string>("0px");
  useEffect(() => {
    if (session.phase !== "open") return;
    const el = headerRef.current;
    if (!el) return;
    const measure = () => {
      const h = `${Math.ceil(el.getBoundingClientRect().height)}px`;
      setHeaderHeight((prev) => (prev === h ? prev : h));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [session.phase, menu.loaded, resto?.image?.path, restaurantName]);

  useEffect(() => {
    const ls = history.lastSentAt;
    if (lastSentAtRef.current === null) {
      lastSentAtRef.current = ls ?? 0;
      return;
    }
    if (ls && ls > (lastSentAtRef.current ?? 0)) {
      lastSentAtRef.current = ls;
      setOrderSentOpen(true);
    }
  }, [history.lastSentAt]);

  const draftLines = useMemo(() => Object.values(cart.lines), [cart.lines]);

  // Quantité au panier par produit — badge sur la carte (tablette Restaurant.js:447-451).
  const cartQtyByProduct = useMemo(() => {
    const m: Record<string, number> = {};
    for (const l of Object.values(cart.lines)) m[l.productId] = (m[l.productId] || 0) + (l.qty || 0);
    return m;
  }, [cart.lines]);

  const { itemCount, cartTotal } = useMemo(() => {
    const lines = Object.values(cart.lines);
    return {
      itemCount: lines.reduce((s, l) => s + (l.qty || 0), 0),
      cartTotal: lines.reduce((s, l) => s + computeLineSubtotal(l), 0),
    };
  }, [cart.lines]);

  const gate = useSubmitGate({
    online,
    isFrozen: isFrozen || submitting,
    draftLines,
    customerNb: session.qrCode?.maxCustomer ?? 1,
    productsById: menu.productsById,
    categoriesById: menu.categoriesById,
    quantityHistory: history.quantityHistory,
    categoryQuantityHistory: history.categoryQuantityHistory,
    orderHistory: history.orderHistory,
    firestoreOrders: orders,
    lastSentAt: history.lastSentAt,
    restaurant: resto,
    activeMenuSettings,
    qrCode: session.qrCode,
  });

  const productCountByCat = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of menu.productsById.values()) {
      const cat = p.categoryId || "_uncategorized";
      counts[cat] = (counts[cat] || 0) + 1;
    }
    return counts;
  }, [menu.productsById]);

  const sortedCategories = useMemo(
    () =>
      Array.from(menu.categoriesById.values()).sort(
        (a, b) => (a.index ?? 0) - (b.index ?? 0)
      ),
    [menu.categoriesById]
  );

  const hasAllergens = useMemo(() => {
    for (const p of menu.productsById.values()) {
      if (((p as any).allergene as string[] | undefined)?.length) return true;
    }
    return false;
  }, [menu.productsById]);

  const enabledServices = useMemo(
    () => (resto?.services ?? []).filter((s) => s.enabled !== false),
    [resto?.services]
  );

  const langDisplay =
    LANG_DISPLAY[i18n.language] ?? LANG_DISPLAY.fr;

  const nicknamesById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of participants) {
      if (p.nickname) m[p.id] = p.nickname;
    }
    if (participant.nickname) m[participant.id] = participant.nickname;
    return m;
  }, [participants, participant.id, participant.nickname]);

  /**
   * Mirrors the tablet's `Restaurant.canAddOneToCart` global-cap branch
   * (Restaurant.js:1044-1054) for the active product. In forfait mode
   * (`!aLaCarte`), only free items count toward the global limit and only
   * free items are themselves limited; paid extras (drinks, etc.) always
   * pass. In à-la-carte mode every item is counted and limited.
   */
  /**
   * Max qty allowed for an existing cart line — mirrors the tablet's
   * `ProductCartRow.computeMaxQuantity` (src/components/ProductCartRow.js:96-141).
   * Walks the four caps (roundQuantity, product.maxQuantity + history,
   * category.maxQuantity + history, global aLaCarte/free) and returns the
   * lowest, never going below the current line.qty.
   */
  const lineMaxQty = useCallback(
    (line: DraftCartLine): number => {
      const product = menu.productsById.get(line.productId);
      if (!product) return line.qty;
      const customerNb = session.qrCode?.maxCustomer ?? 1;
      const lines = Object.values(cart.lines);
      let cap = 99;

      if (product.roundQuantity && product.roundQuantity > 0) {
        const same = lines.filter((l) => l.productId === product.id);
        const totalQty = same.reduce((s, l) => s + (l.qty || 0), 0);
        cap = Math.min(cap, line.qty + product.roundQuantity * customerNb - totalQty);
      }

      if (product.maxQuantity && product.maxQuantity > 0) {
        const same = lines.filter((l) => l.productId === product.id);
        let totalQty = same.reduce((s, l) => s + (l.qty || 0), 0);
        totalQty += history.quantityHistory?.[product.id] ?? 0;
        cap = Math.min(cap, line.qty + product.maxQuantity * customerNb - totalQty);
      }

      const cat = product.categoryId ? menu.categoriesById.get(product.categoryId) : null;
      if (cat?.maxQuantity && cat.maxQuantity > 0 && product.categoryId) {
        const sameCat = lines.filter((l) => {
          const p = menu.productsById.get(l.productId);
          return p?.categoryId === product.categoryId;
        });
        let totalCatQty = sameCat.reduce((s, l) => s + (l.qty || 0), 0);
        totalCatQty += history.categoryQuantityHistory?.[product.categoryId] ?? 0;
        cap = Math.min(cap, line.qty + cat.maxQuantity * customerNb - totalCatQty);
      }

      const globalMax = activeMenuSettings.maxQuantity;
      if (globalMax > 0) {
        const isFree = (product.unitPrice ?? 0) === 0;
        if (aLaCarte || isFree) {
          const totalCart = aLaCarte
            ? lines.reduce((s, l) => s + (l.qty || 0), 0)
            : lines
                .filter((l) => (l.unitPrice ?? 0) === 0)
                .reduce((s, l) => s + (l.qty || 0), 0);
          cap = Math.min(cap, line.qty + globalMax * customerNb - totalCart);
        }
      }

      return Math.max(cap, line.qty);
    },
    [
      cart.lines,
      menu.productsById,
      menu.categoriesById,
      activeMenuSettings.maxQuantity,
      aLaCarte,
      session.qrCode?.maxCustomer,
      history.quantityHistory,
      history.categoryQuantityHistory,
    ]
  );

  /**
   * Max additional units of `product` that can be added to the cart in one go,
   * given all four caps the tablet enforces in `Restaurant.canAddOneToCart`
   * (Restaurant.js:1021-1057): roundQuantity, product.maxQuantity (+ history),
   * category.maxQuantity (+ history), and the global aLaCarte/free cap.
   * Returns 0 when the user cannot add anything for this product right now.
   */
  const maxAddable = (product: Product): number => {
    const customerNb = session.qrCode?.maxCustomer ?? 1;
    const lines = Object.values(cart.lines);
    let cap = 99;

    if (product.roundQuantity && product.roundQuantity > 0) {
      const same = lines.filter((l) => l.productId === product.id);
      const totalQty = same.reduce((s, l) => s + (l.qty || 0), 0);
      cap = Math.min(cap, product.roundQuantity * customerNb - totalQty);
    }

    if (product.maxQuantity && product.maxQuantity > 0) {
      const same = lines.filter((l) => l.productId === product.id);
      let totalQty = same.reduce((s, l) => s + (l.qty || 0), 0);
      totalQty += history.quantityHistory?.[product.id] ?? 0;
      cap = Math.min(cap, product.maxQuantity * customerNb - totalQty);
    }

    const cat = product.categoryId ? menu.categoriesById.get(product.categoryId) : null;
    if (cat?.maxQuantity && cat.maxQuantity > 0 && product.categoryId) {
      const sameCat = lines.filter((l) => {
        const p = menu.productsById.get(l.productId);
        return p?.categoryId === product.categoryId;
      });
      let totalCatQty = sameCat.reduce((s, l) => s + (l.qty || 0), 0);
      totalCatQty += history.categoryQuantityHistory?.[product.categoryId] ?? 0;
      cap = Math.min(cap, cat.maxQuantity * customerNb - totalCatQty);
    }

    const globalMax = activeMenuSettings.maxQuantity;
    if (globalMax > 0) {
      const isFree = (product.unitPrice ?? 0) === 0;
      if (aLaCarte || isFree) {
        const totalCart = aLaCarte
          ? lines.reduce((s, l) => s + (l.qty || 0), 0)
          : lines
              .filter((l) => (l.unitPrice ?? 0) === 0)
              .reduce((s, l) => s + (l.qty || 0), 0);
        cap = Math.min(cap, globalMax * customerNb - totalCart);
      }
    }

    return Math.max(cap, 0);
  };

  const canAddQty = (product: Product, qty: number): boolean =>
    qty <= maxAddable(product);

  /**
   * Per-product remaining/cap pairs for the round and table-wide limits.
   * Mirrors the tablet's `ProductDetail.getRoundQuantity` / `getMaxQuantity`
   * (src/components/ProductDetail.js:208-244). Each pair is null when the
   * corresponding cap isn't configured on the product.
   */
  const productLimits = (product: Product): {
    roundRemaining: number | null;
    roundCap: number | null;
    totalRemaining: number | null;
    totalCap: number | null;
  } => {
    const customerNb = session.qrCode?.maxCustomer ?? 1;
    const lines = Object.values(cart.lines);
    const totalProductQty = lines
      .filter((l) => l.productId === product.id)
      .reduce((s, l) => s + (l.qty || 0), 0);

    const roundCap =
      product.roundQuantity && product.roundQuantity > 0
        ? product.roundQuantity * customerNb
        : null;
    const roundRemaining =
      roundCap != null ? Math.max(0, roundCap - totalProductQty) : null;

    const totalCap =
      product.maxQuantity && product.maxQuantity > 0
        ? product.maxQuantity * customerNb
        : null;
    const totalRemaining =
      totalCap != null
        ? Math.max(
            0,
            totalCap -
              totalProductQty -
              (history.quantityHistory?.[product.id] ?? 0)
          )
        : null;

    return { roundRemaining, roundCap, totalRemaining, totalCap };
  };

  const headerMetrics = useMemo(() => {
    const lines = Object.values(cart.lines);
    const totalProducts = lines.reduce((s, l) => s + (l.qty || 0), 0);
    const totalFreeProducts = lines
      .filter((l) => (l.unitPrice ?? 0) === 0)
      .reduce((s, l) => s + (l.qty || 0), 0);
    const customerNb = session.qrCode?.maxCustomer ?? 1;

    const remainingTurns =
      activeMenuSettings.maxOrders > 0
        ? Math.max(0, activeMenuSettings.maxOrders - countBuffetTours(orders))
        : null;

    const remainingQuantity =
      activeMenuSettings.maxQuantity > 0
        ? Math.max(
            0,
            activeMenuSettings.maxQuantity * customerNb -
              (aLaCarte ? totalProducts : totalFreeProducts)
          )
        : null;

    return { remainingTurns, remainingQuantity };
  }, [
    cart.lines,
    activeMenuSettings.maxOrders,
    activeMenuSettings.maxQuantity,
    orders.length,
    session.qrCode?.maxCustomer,
    aLaCarte,
  ]);

  if (session.phase === "loading" || (!menu.loaded && session.phase === "open")) {
    return <SplashScreen restaurantName={restaurantName ?? resto?.id} />;
  }
  if (session.phase === "invalid") return <InvalidQrPage />;
  if (session.phase === "closed") return <InvalidQrPage variant="closed" />;

  const onSelectCategory = (categoryId: string): void => {
    setCategoryPickerOpen(false);
    requestAnimationFrame(() => {
      document
        .getElementById(`section-${categoryId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const onAddFromModal = (line: { qty: number; unitPrice: number; options: SelectedRubric[] }): void => {
    if (!detailProduct) return;
    if (!canAddQty(detailProduct, line.qty)) {
      setLimitAlert(t("error.maxTotal", "La quantité maximum pour ce produit est atteinte"));
      return;
    }
    void cart.addLine({
      productId: detailProduct.id,
      qty: line.qty,
      unitPrice: line.unitPrice,
      options: line.options,
      productSnapshot: {
        name: detailProduct.name,
        description: detailProduct.description,
        image: detailProduct.image,
        classification: detailProduct.classification,
        categoryId: detailProduct.categoryId,
      },
    });
    setDetailProduct(null);
  };

  /**
   * Ajout rapide depuis la carte — tablette handleQuickAdd (Restaurant.js:1056-1140) :
   * modal seulement si un choix est obligatoire ; sinon +1 sur ma ligne sans
   * option du même produit, ou nouvelle ligne.
   */
  const onQuickAdd = (product: Product, sourceEl: Element | null): void => {
    // Un produit composé passe toujours par ses choix
    if (!rubricCatalog.loaded || hasRequiredChoice(product, rubricCatalog) || hasComposition(product)) {
      detailSourceRef.current = sourceEl;
      setDetailProduct(product);
      return;
    }
    if (!canAddQty(product, 1)) {
      setLimitAlert(t("error.maxTotal", "La quantité maximum pour ce produit est atteinte"));
      return;
    }
    const existing = Object.entries(cart.lines).find(
      ([, l]) => l.productId === product.id && l.addedBy === participantId && !(l.options?.length)
    );
    if (existing) {
      void cart.updateQty(existing[0], existing[1].qty + 1);
    } else {
      void cart.addLine({
        productId: product.id,
        qty: 1,
        unitPrice: product.unitPrice,
        productSnapshot: {
          name: product.name,
          description: product.description,
          image: product.image,
          classification: product.classification,
          categoryId: product.categoryId,
        },
      });
    }
    flyImageToCart(sourceEl, withImageVersion(product.image?.path), product.name);
  };

  const onSubmitConfirmed = async (waiterPassword?: string): Promise<void> => {
    if (!parsed) return;
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      await callTriggerSubmit({
        sessionId: parsed.sessionId,
        source: "phone",
        qrToken: parsed.qrToken ?? undefined,
        waiterPassword,
      });
      // Close the cart drawer so the countdown overlay is visually unobstructed.
      setCartOpen(false);
    } catch (err) {
      console.error("trigger failed", err);
      // HttpsError(code, failure.code) : le message porte le code métier.
      const code = (err as { message?: string } | null)?.message ?? "";
      const messages: Record<string, string> = {
        product_unavailable: t(
          "error.productUnavailable",
          "Un produit du panier n'est plus disponible à cette heure. Retirez-le puis réessayez."
        ),
        waiter_password_invalid: t("error.waiterPassword", "Code serveur incorrect."),
        ordering_suspended: t("submit.canOrderOff", "Commandes suspendues"),
        max_orders: t("submit.maxOrders", "Limite de tournées atteinte"),
        cooldown_active: t("modalCart.pleaseWait", "Veuillez patienter"),
      };
      setLimitAlert(messages[code] ?? t("error.submitFailed", "Échec de l'envoi, réessayez."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConnectivityGuard>
      <div style={{ "--header-h": headerHeight } as CSSProperties}>
        <InAppBrowserBanner />
        <header
          ref={headerRef}
          className="sticky top-0 z-30 bg-bg/95 backdrop-blur-md border-b border-border px-5 !pt-1 !pb-1 sm:pt-14 sm:pb-5 flex items-center gap-3 pt-safe"
        >
          {headerLogo ? (
            <img
              src={withImageVersion(headerLogo)}
              alt={restaurantName ?? ""}
              onError={() => setBrokenLogo(headerLogo)}
              className="h-11 max-w-[120px] object-contain shrink-0"
            />
          ) : (
            <h1 className="display-font text-base sm:text-lg font-semibold text-ink leading-tight line-clamp-2 break-words flex-1 min-w-0">
              {restaurantName ?? resto?.id ?? ""}
            </h1>
          )}

          <div className={`flex items-center gap-3 ${headerLogo ? "flex-1 justify-start" : ""}`}>
            {cartShown && session.qrCode?.table != null && session.qrCode.table !== "" && (
              <div className="flex flex-col items-center leading-tight px-2">
                <span className="text-[10px] uppercase tracking-wider text-ink-muted">
                  {t("header.table", "Table")}
                </span>
                <span className="display-font text-base font-semibold text-ink tabular-nums">
                  {session.qrCode.table}
                </span>
              </div>
            )}
            {cartShown && session.qrCode?.maxCustomer ? (
              <div className="flex flex-col items-center leading-tight px-2">
                <span className="text-[10px] uppercase tracking-wider text-ink-muted">
                  {t("header.guests", "Couverts")}
                </span>
                <span className="display-font text-base font-semibold text-ink tabular-nums">
                  {session.qrCode.maxCustomer}
                </span>
              </div>
            ) : null}
          </div>

          <div className="flex items-center gap-2 shrink-0 ml-auto">
            <button
              type="button"
              onClick={() => setMainMenuOpen(true)}
              aria-label={t("mainMenu.openAria", "Ouvrir le menu")}
              className="w-11 h-11 rounded-full bg-surface border border-border text-ink flex flex-col items-center justify-center gap-[3px]"
            >
              <span className="block w-4 h-[2px] rounded bg-ink" aria-hidden="true" />
              <span className="block w-4 h-[2px] rounded bg-ink" aria-hidden="true" />
              <span className="block w-4 h-[2px] rounded bg-ink" aria-hidden="true" />
            </button>
          </div>
        </header>

        {cartShown && (
          <div
            className="fixed right-4 z-30 sm:right-6"
            style={{ bottom: "max(env(safe-area-inset-bottom, 0px), 16px)" }}
          >
            <CartTriggerButton
              onClick={() => setCartOpen(true)}
              itemCount={itemCount}
              total={cartTotal}
            />
          </div>
        )}

        <main>
          <MenuList
            productsById={menu.productsById}
            categoriesById={menu.categoriesById}
            isFrozen={isFrozen}
            readOnly={!cartShown}
            cartQtyByProduct={cartQtyByProduct}
            canQuickAdd={(p) => canAddQty(p, 1)}
            onOpen={(p, sourceEl) => {
              detailSourceRef.current = sourceEl;
              setDetailProduct(p);
            }}
            onQuickAdd={onQuickAdd}
            onOpenCategoryPicker={() => setCategoryPickerOpen(true)}
            layout={categoryLayout}
          />
        </main>
      </div>

      <SharedCartView
        open={cartShown && cartOpen}
        onClose={() => setCartOpen(false)}
        lines={cart.lines}
        productsById={menu.productsById}
        nicknamesById={nicknamesById}
        lineMaxQty={lineMaxQty}
        isFrozen={isFrozen}
        myParticipantId={participantId}
        onUpdateQty={(lineId, qty) => {
          if (qty <= 0) removal.markSelfRemoved(lineId);
          void cart.updateQty(lineId, qty);
        }}
        onRemoveLine={(lineId) => {
          removal.markSelfRemoved(lineId);
          const line = cart.lines[lineId];
          const isMine = line?.addedBy === participantId;
          const byName = !isMine ? participant.nickname ?? undefined : undefined;
          void cart.removeLine(lineId, byName);
        }}
        onSubmit={() => setConfirmOpen(true)}
        submitDisabled={!gate.canSubmit}
        submitLabel={gate.cta}
        submitHint={gate.message || undefined}
        deadlineNotice={gate.deadlineNotice}
      />

      <ConfirmSubmitDialog
        open={cartShown && confirmOpen}
        itemCount={itemCount}
        total={cartTotal}
        requireWaiterPassword={waiterConfirm}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={(pwd) => void onSubmitConfirmed(pwd)}
      />

      <CategoryPicker
        open={categoryPickerOpen}
        onClose={() => setCategoryPickerOpen(false)}
        categories={sortedCategories}
        productCountByCat={productCountByCat}
        onSelect={onSelectCategory}
      />

      <ProductDetailModal
        product={detailProduct}
        rubrics={resolveProductRubrics(detailProduct, rubricCatalog)}
        rubricsLoading={!rubricCatalog.loaded}
        sourceEl={detailSourceRef.current}
        readOnly={!cartShown}
        open={!!detailProduct}
        onClose={() => setDetailProduct(null)}
        onAdd={onAddFromModal}
        canAddQty={(qty) => (detailProduct ? canAddQty(detailProduct, qty) : true)}
        maxAddable={() => (detailProduct ? maxAddable(detailProduct) : 99)}
        productLimits={detailProduct ? productLimits(detailProduct) : undefined}
        products={menu.choosableById}
        resolveOptions={(p) => resolveProductRubrics(p, rubricCatalog)}
      />

      <AlertDialog
        open={!!limitAlert}
        title={t("error.title", "Erreur")}
        description={limitAlert ?? undefined}
        onClose={() => setLimitAlert(null)}
      />

      <MainMenuDrawer
        open={mainMenuOpen}
        onClose={() => setMainMenuOpen(false)}
        orderCount={orders.length}
        hasServices={enabledServices.length > 0}
        hasAllergens={hasAllergens}
        currentLangFlag={langDisplay.flag}
        currentLangLabel={langDisplay.label}
        remainingTurns={headerMetrics.remainingTurns}
        remainingQuantity={headerMetrics.remainingQuantity}
        cooldownRemaining={gate.cooldownRemaining ?? null}
        showOrdering={cartShown}
        onOpenLanguage={() => {
          setMainMenuOpen(false);
          setLangOpen(true);
        }}
        onOpenAllergen={() => {
          setMainMenuOpen(false);
          setAllergenOpen(true);
        }}
        onOpenService={() => {
          setMainMenuOpen(false);
          setServiceOpen(true);
        }}
        onOpenHistory={() => {
          setMainMenuOpen(false);
          setHistoryOpen(true);
        }}
      />

      <LanguagePicker
        open={langOpen}
        onClose={() => setLangOpen(false)}
        languagesConfig={resto?.settings?.languages ?? null}
      />

      <AllergenModal
        open={allergenOpen}
        onClose={() => setAllergenOpen(false)}
        productsById={menu.productsById}
      />

      {parsed && (
        <ServiceModal
          open={serviceOpen}
          onClose={() => setServiceOpen(false)}
          restoId={parsed.restoId}
          qrId={parsed.qrId}
          table={session.qrCode?.table}
          services={enabledServices}
        />
      )}

      <OrderHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        orders={orders}
      />

      <ErrorToast errors={session.errors} />
      <NoticeToast notices={removal.notices} onDismiss={removal.dismiss} />
      <OrderSentToast open={orderSentOpen} onDismiss={closeOrderSent} />

      <NicknameModal
        open={session.phase === "open" && menu.loaded && !participant.nickname}
        onSubmit={(name) => void participant.setNickname(name)}
      />

      {session.pendingSubmit && (
        <CountdownOverlay
          sessionId={parsed!.sessionId}
          pendingSubmit={session.pendingSubmit}
          myParticipantId={participantId}
        />
      )}
    </ConnectivityGuard>
  );
};
